import {
    EmployeeGet,
    EmployeeGetExtResponse,
    IBufferedCachingList,
    SubscribeState,
    TimeContractGetResponse,
    Ulid,
} from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import { dateFromApi, upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    Employee,
    EmployeeFeeCode,
    GetHourlyRateParams,
    TimeContract,
} from "./interfaces";
import { getContractKey, isContractKey } from "./utils";

const enum ContractState {
    Started = 1,
    Finished,
}

export const makeEmployee = ({ connection, company }: ApiSliceParams) => {
    const contractsState: Record<string, ContractState> = {};
    const contractsStore: Record<string, TimeContract[]> = {};
    const listeningCodesFor: string[] = [];
    let autoListenCodes = false;

    const isContractsLoading = (id: string) => {
        for (const key in contractsState) {
            if (
                isContractKey(id, key) &&
                contractsState[key] === ContractState.Started
            ) {
                return true;
            }
        }

        return false;
    };

    const view = (employee: EmployeeGet) => {
        const id = employee.id.toString();

        const finalEmployee: Employee = {
            ...employee,
            id,
            _id: employee.id,
            feeCodes: signal([]),
            loadingContracts: signal(isContractsLoading(id)),
            loadingActivities: signal(false),
            contracts: signal(contractsStore[id] ?? []),
            contractsFailed: signal(false),
        };

        if (contractsStore[id]) {
            delete contractsStore[id];
        }

        if (autoListenCodes && !listeningCodesFor.includes(id)) {
            listenCodesFor([finalEmployee]);
        }

        return finalEmployee;
    };

    const { emit, on, off } = createEmitter<Employee>();

    const {
        items: employees,
        allLoading: loading,
        allLoaded: loaded,
        listen,
        whenLoaded,
        get,
        getAndSub,
        getOrLoad,
    } = createListController<EmployeeGet, Employee>({
        name: "employee",
        connection,
        list: (con) => con.employee.items({ company: company.value }),
        view,
        emit,
    });

    const me = computed(
        () =>
            employees.value.find(
                (e) =>
                    e.preferred_username ===
                    connection.value?.user.preferred_username,
            ) ?? null,
    );

    const bindContracts = (
        employeeId: Readonly<Ulid>,
        contracts: TimeContract[],
    ) => {
        const employee = employees.peek().find((e) => e._id.equals(employeeId));

        if (employee) {
            employee.contracts.value = upsert(
                employee.contracts.peek().slice(),
                contracts,
                "id",
            );
            employee.loadingContracts.value = false;
        } else {
            const id = employeeId.toString();

            if (!(id in contractsStore)) {
                contractsStore[id] = [];
            }

            contractsStore[id] = upsert(
                contractsStore[id].slice(),
                contracts,
                "id",
            );
        }
    };

    const loadFinalContracts = async (
        list: IBufferedCachingList<Ulid, TimeContractGetResponse>,
        employeeId: Readonly<Ulid>,
        from: Date | null,
        to: Date | null,
    ) => {
        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                bindContracts(
                    employeeId,
                    event.values.map((item) => ({
                        ...item,
                        _id: item.id,
                        id: item.id.toString(),
                        from,
                        to,
                    })),
                );
            } else if (event.kind === SubscribeState.Loaded) {
            }
        }
    };

    const loadContract = async (
        employeeId: Readonly<Ulid>,
        num: number,
        year: number,
        from: Date | null,
        to: Date | null,
    ) => {
        const con = connection.value;
        const result: TimeContract[] = [];
        const list = con.time.contract.items({
            company: company.value,
            id: num,
            year,
        });

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                bindContracts(
                    employeeId,
                    event.values.map((item) => ({
                        ...item,
                        _id: item.id,
                        id: item.id.toString(),
                        from,
                        to,
                    })),
                );
            } else if (
                event.kind === SubscribeState.LoadedCache ||
                event.kind === SubscribeState.Loaded
            ) {
                break;
            } else if (event.kind === SubscribeState.Error) {
                throw new Error("Contracts loading error (items)");
            }
        }

        loadFinalContracts(list, employeeId, from, to);

        return result;
    };

    const loadContractExt = (
        employeeId: Readonly<Ulid>,
        extension: EmployeeGetExtResponse,
        year: number,
    ) => {
        return extension.calendar_fields
            .filter(
                ({ calendar_from, calendar_to }) =>
                    !calendar_from ||
                    !calendar_to ||
                    (dateFromApi(calendar_from).getFullYear() <= year &&
                        dateFromApi(calendar_to).getFullYear() >= year),
            )
            .map((field) =>
                loadContract(
                    employeeId,
                    field.calendar_num,
                    year,
                    dateFromApi(field.calendar_from),
                    dateFromApi(field.calendar_to),
                ),
            );
    };

    const loadFinalContractExt = async (
        list: IBufferedCachingList<Ulid, EmployeeGetExtResponse>,
        employeeId: Readonly<Ulid>,
        year: number,
    ) => {
        const promises: Promise<unknown>[] = [];

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                promises.push(
                    ...event.values.flatMap((extension) =>
                        loadContractExt(employeeId, extension, year),
                    ),
                );
            } else if (event.kind === SubscribeState.Loaded) {
                break;
            }
        }

        await Promise.all(promises);
    };

    const tryLoadContracts = async (
        employeeId: Readonly<Ulid>,
        year: number,
    ) => {
        const con = connection.value;
        const promises: Promise<unknown>[] = [];
        const employeeBefore = employees
            .peek()
            .find((e) => e._id.equals(employeeId));

        if (employeeBefore) {
            employeeBefore.loadingContracts.value = true;
        }

        const list = con.employee.itemsExt({
            company: company.value,
            id: [employeeId],
        });

        for await (const event of list) {
            if (
                event.kind === SubscribeState.Add ||
                event.kind === SubscribeState.Update
            ) {
                promises.push(
                    ...event.values.flatMap((extension) =>
                        loadContractExt(employeeId, extension, year),
                    ),
                );
            } else if (
                event.kind === SubscribeState.LoadedCache ||
                event.kind === SubscribeState.Loaded
            ) {
                break;
            }
        }

        loadFinalContractExt(list, employeeId, year);

        await Promise.all(promises);
    };

    const loadContracts = async (employeeId: Readonly<Ulid>, year: number) => {
        const key = getContractKey(employeeId, year);

        if (contractsState[key]) {
            return;
        }

        contractsState[key] = ContractState.Started;

        let tryCount = 0;
        let success = false;

        do {
            try {
                await tryLoadContracts(employeeId, year);
                success = true;
            } catch (error: unknown) {
                console.log(
                    "Error occurred while trying to load contracts",
                    error,
                );
            }

            ++tryCount;
        } while (tryCount < 3 && !success);

        contractsState[key] = ContractState.Finished;

        const employee = employees.peek().find((e) => e._id.equals(employeeId));

        if (!employee) {
            return;
        }

        if (success) {
            employee.loadingContracts.value = false;
        } else {
            employee.contractsFailed.value = true;
        }
    };

    const listenCodesFor = async (employees: Employee[]) => {
        const con = connection.value;

        employees.forEach((employee) => {
            employee.loadingActivities.value = true;
            listeningCodesFor.push(employee.id);
        });

        for await (const event of con.employee.feecode.items({
            company: company.value,
            employee: employees.map((e) => e._id),
        })) {
            if (
                event.kind === SubscribeState.Loaded ||
                event.kind === SubscribeState.LoadedCache
            ) {
                employees.forEach(
                    (employee) => (employee.loadingActivities.value = false),
                );
            } else if (event.kind === SubscribeState.Remove) {
                event.values.forEach((code) => {
                    employees.forEach((employee) => {
                        const idx = employee.feeCodes
                            .peek()
                            .findIndex((c) => c._id.equals(code.id));

                        if (idx !== -1) {
                            employee.feeCodes.value = [
                                ...employee.feeCodes.peek().slice(0, idx),
                                ...employee.feeCodes.peek().slice(idx + 1),
                            ];
                        }
                    });
                });
            } else {
                const grouped = event.values.reduce(
                    (acc, code) => {
                        const employeeId = code.employee.toString();

                        if (!(employeeId in acc)) {
                            acc[employeeId] = [];
                        }

                        acc[employeeId].push({
                            _id: code.id,
                            id: code.id.toString(),
                            employee: code.employee,
                            code: code.fee_code,
                        });

                        return acc;
                    },
                    {} as Record<string, EmployeeFeeCode[]>,
                );

                for (const employeeId in grouped) {
                    const employee = employees.find((e) => e.id === employeeId);

                    if (employee) {
                        employee.feeCodes.value = upsert(
                            employee.feeCodes.peek().slice(),
                            grouped[employeeId],
                            "id",
                        );
                    }
                }
            }
        }
    };

    const listenCodes = () => {
        autoListenCodes = true;

        listenCodesFor(
            employees.peek().filter((e) => !listeningCodesFor.includes(e.id)),
        );

        return () => {
            autoListenCodes = false;
        };
    };

    const rate = async (params: GetHourlyRateParams) => {
        const con = connection.value;
        const result = await con.employee.hourlyRate({
            company: company.value,
            ...params,
        });

        return result.rate?.toNumber();
    };

    const getByUsername = (username: string | null | undefined) => {
        if (!username) {
            return null;
        }

        return (
            employees.value.find((e) => e.preferred_username === username) ??
            null
        );
    };

    return {
        me,
        employees,
        loading,
        loaded,
        on,
        off,
        listen,
        whenLoaded,
        rate,
        listenCodes,
        listenCodesFor,
        get,
        getAndSub,
        getOrLoad,
        getByUsername,
        loadContracts,
    };
};
